<script lang="ts" module>
  // Select — the design-system dropdown primitive (wiki/feature-design-system.md
  // §Component primitives). A subtle, styled native `<select>` for a compact
  // single-select where a SegmentedToggle would be too wide (e.g. the video-
  // quality picker, which sits inline beside the background-blur toggle on a
  // narrow record column). Mirrors SegmentedToggle's prop shape — a typed
  // `options` list, a bindable `value` + `onChange`, `disabled`, an accessible
  // label, and a `class` passthrough — so the two are drop-in interchangeable.
  // The native element keeps full keyboard + a11y semantics; the chrome stays
  // hairline + token-driven so it reads as a quiet control, not a primary action.
  export interface SelectOption {
    /** The value committed to `bind:value` when this option is chosen. */
    value: string;
    /** The visible label. */
    label: string;
  }

  /** A named `<optgroup>` bucket, for a list that reads as tiers. */
  export interface SelectGroup {
    label: string;
    options: SelectOption[];
  }
</script>

<script lang="ts">
  interface Props {
    /** The selectable options. Ignored when `groups` is given. */
    options?: SelectOption[];
    /** Grouped options, rendered as `<optgroup>`s. Takes precedence. */
    groups?: SelectGroup[];
    /** The selected value (bindable). */
    value: string;
    /** Fired after the selection changes to a new value. */
    onChange?: (value: string) => void;
    /** Disable the control. */
    disabled?: boolean;
    /** Visible field label; renders TextField's label + control size so the two share a form row. */
    label?: string;
    /** Shown after the label in its own case, e.g. "VOICE (female)". */
    labelNote?: string;
    /** Accessible label (default "Select"). */
    'aria-label'?: string;
    /** Id of the element that labels this control — wins over `aria-label`. */
    'aria-labelledby'?: string;
    /** Id of the element that describes this control. */
    'aria-describedby'?: string;
    /** Id of the popup this control opens. */
    'aria-controls'?: string;
    'data-testid'?: string;
    class?: string;
  }

  // eslint-plugin-svelte 3.23.0 `no-unused-props` misses a quoted prop key whose
  // destructure carries a default: getUsedPropertyNamesFromPattern reads a Literal
  // key only when the value is a bare Identifier, never an AssignmentPattern. The
  // default lives at the use site until that is fixed upstream.
  let {
    options = [],
    groups,
    value = $bindable(),
    onChange,
    disabled = false,
    label,
    labelNote,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-controls': ariaControls,
    'data-testid': dataTestid,
    class: klass = '',
  }: Props = $props();

  const selectId = `vd-select-${Math.random().toString(36).slice(2, 9)}`;

  // Native `change` commits the new value; mirror it out via onChange. The
  // `bind:value` already wrote `value`, so we forward the same string.
  function onchange(): void {
    onChange?.(value);
  }
</script>

<div class="vd-select {klass}" class:vd-select--field={!!label}>
  {#if label}<label class="vd-select__label" for={selectId}
      >{label}{#if labelNote}<span class="vd-select__note">{` (${labelNote})`}</span>{/if}</label
    >{/if}
  <div class="vd-select__box">
    <select
      id={selectId}
      class="vd-select__el"
      bind:value
      {onchange}
      {disabled}
      aria-label={ariaLabelledBy ? undefined : (ariaLabel ?? (label ? undefined : 'Select'))}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-controls={ariaControls}
      data-testid={dataTestid}
    >
      {#if groups}
        {#each groups as group (group.label)}
          <optgroup label={group.label}>
            {#each group.options as option (option.value)}
              <option value={option.value}>{option.label}</option>
            {/each}
          </optgroup>
        {/each}
      {:else}
        {#each options as option (option.value)}
          <option value={option.value}>{option.label}</option>
        {/each}
      {/if}
    </select>
    <!-- Decorative chevron — the native arrow is hidden via appearance:none so
         the control matches the design system rather than the OS chrome. -->
    <span class="vd-select__chevron" aria-hidden="true">▾</span>
  </div>
</div>

<style lang="scss">
  .vd-select {
    display: inline-flex;
    flex-direction: column;
    gap: 0.35rem;
    max-width: 100%;
    min-width: 0;
  }

  // A labelled field fills its container like TextField; the compact control stays shrink-to-fit.
  .vd-select--field {
    display: flex;
  }

  .vd-select__box {
    position: relative;
    display: flex;
    align-items: center;
  }

  // Mirrors TextField's label + control metrics, so a labelled Select and a
  // TextField line up label-to-label and edge-to-edge in one form row.
  .vd-select__label {
    font-family: var(--font-ui);
    font-size: var(--text-label);
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: var(--ink-mute);
  }

  .vd-select__note {
    letter-spacing: normal;
    text-transform: none;
  }

  .vd-select--field .vd-select__el {
    padding: var(--field-pad-y) 2rem var(--field-pad-y) var(--field-pad-x);
    font-family: var(--font-ui);
    font-size: var(--text-field);
    line-height: 1.5;
    border-radius: var(--field-radius);
  }

  .vd-select__el {
    font-family: var(--font-control);
    font-size: var(--text-control);
    line-height: 1;
    // The segmented track's inset on top of the button padding, so the closed box
    // is as tall as a SegmentedToggle; 1rem extra on the right clears the chevron.
    padding: calc(var(--control-pad-top) + var(--control-track))
      calc(var(--control-pad-x) + var(--control-track) + 1rem)
      calc(var(--control-pad-bottom) + var(--control-track))
      calc(var(--control-pad-x) + var(--control-track));
    // Fill the box so the absolutely placed chevron always sits inside the field.
    flex: 1;
    min-width: 0;
    max-width: 100%;
    border: 1px solid var(--line);
    border-radius: var(--control-radius);
    background: var(--paper);
    color: var(--ink);
    cursor: pointer;
    // Hide the native arrow so our own chevron + tokens drive the chrome.
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    transition:
      border-color 0.2s ease,
      background 0.2s ease;

    &:hover:not(:disabled) {
      border-color: color-mix(in srgb, var(--accent) 45%, var(--line));
    }

    &:focus-visible {
      outline: 2px solid var(--accent-text);
      outline-offset: 2px;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }
  }

  // The chevron sits over the right padding; pointer-events:none so the click
  // still falls through to the native select.
  .vd-select__chevron {
    position: absolute;
    right: var(--control-pad-x);
    font-size: 0.7rem;
    color: var(--ink-mute);
    pointer-events: none;
  }

  .vd-select--field .vd-select__chevron {
    right: var(--field-pad-x);
  }

  // The same 44px touch floor the Button primitive keeps.
  @media (pointer: coarse) {
    .vd-select__el {
      min-height: 44px;
    }
  }
</style>
